import gql from 'graphql-tag';
import { toast } from 'react-toastify';
import client from '../../../utils/apolloClient';
import { upload_campaign_csv } from '../../../utils/s3';

const getError = res =>
  Array.isArray(res?.error) && res.error[0] ? res.error[0].description : null;

const createNotificationCampaignMutation = gql`
  mutation createNotificationCampaignMutation(
    $input: NotificationCampaignInput
  ) {
    create_notification_campaign(input: $input) {
      user_id
      title
      template_id
      platform
      source
      csv_key
      csv_url
      total_recipients
      sent_success
      sent_failed
      status
      reply_to
      copy_emails
      error {
        code
        description
      }
      audit {
        created_at
        created_by
        updated_at
        updated_by
      }
    }
  }
`;

const updateNotificationCampaignMutation = gql`
  mutation updateNotificationCampaignMutation(
    $input: NotificationCampaignInput
  ) {
    update_notification_campaign(input: $input) {
      user_id
      title
      template_id
      platform
      source
      csv_key
      csv_url
      total_recipients
      sent_success
      sent_failed
      status
      reply_to
      copy_emails
      error {
        code
        description
      }
      audit {
        created_at
        created_by
        updated_at
        updated_by
      }
    }
  }
`;

export const createNotificationCampaign = async ({ input }) => {
  try {
    const {
      data: { create_notification_campaign },
    } = await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation: createNotificationCampaignMutation,
      variables: { input },
    });
    return getError(create_notification_campaign);
  } catch (error) {
    return 'Error : UNEXPECTED NETWORK ERROR';
  }
};

export const updateNotificationCampaign = async ({ input }) => {
  try {
    const {
      data: { update_notification_campaign },
    } = await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation: updateNotificationCampaignMutation,
      variables: { input },
    });
    return getError(update_notification_campaign);
  } catch (error) {
    return 'Error : UNEXPECTED NETWORK ERROR';
  }
};

export const searchNotificationCampaignsQuery = gql`
  query searchNotificationCampaignsQuery($input: SearchInput) {
    search_notification_campaigns(input: $input) {
      total_pages
      total_size
      notification_campaign_listing {
        csv_url
        csv_key
        notification_campaign_id
        user_id
        total_recipients
        title
        template_id
        status
        source
        sent_success
        sent_failed
        reply_to
        platform
        copy_emails
        audit {
          created_at
          created_by
          updated_at
          updated_by
        }
      }
    }
  }
`;

export const fetchNotificationCampaigns = async ({ userId, templateId }) => {
  try {
    const {
      data: { search_notification_campaigns },
    } = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchNotificationCampaignsQuery,
      variables: {
        input: {
          user_id: userId,
          filter: {
            notification_campaign_filter: {
              notification_campaign_id: templateId,
            },
          },
        },
      },
    });

    if (
      search_notification_campaigns &&
      search_notification_campaigns?.notification_campaign_listing &&
      Array.isArray(search_notification_campaigns.notification_campaign_listing)
    ) {
      return search_notification_campaigns.notification_campaign_listing.find(
        ele => ele.notification_campaign_id === templateId,
      );
    }
    return null;
  } catch (error) {
    return null;
  }
};

const startNotificationCampaignMutation = gql`
  mutation startNotificationCampaignMutation(
    $userId: String
    $status: String
    $campaignId: String
  ) {
    start_notification_campaign(
      input: {
        user_id: $userId
        status: $status
        notification_campaign_id: $campaignId
      }
    ) {
      error {
        code
        description
      }
      message
    }
  }
`;

export const startNotificationCampaign = async ({ userId, campaignId }) => {
  try {
    const {
      data: { start_notification_campaign },
    } = await client.clientPrivate.mutate({
      client: client.clientPrivate,
      mutation: startNotificationCampaignMutation,
      variables: { userId, status: 'IN_PROGRESS', campaignId },
    });
    return {
      data: start_notification_campaign?.message,
      error: getError(start_notification_campaign),
    };
  } catch (error) {
    return {
      data: null,
      error: 'Error : UNEXPECTED NETWORK ERROR',
    };
  }
};

export const handleNotificationCampaignButtonClick = async ({
  userId,
  campaignId,
}) => {
  const res = await startNotificationCampaign({ userId, campaignId });

  if (res.error) {
    toast.error(res.error);
  } else if (res.data) {
    toast.success(res.data);
  }
};

const uploadFile = ({ file, userId, templateId }) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided!'));
    } else {
      const reader = new FileReader();

      reader.onload = async e => {
        try {
          const fileContent = e.target.result;
          const timestamp = new Date().getTime();
          const fileName = `${userId}-${templateId}-${timestamp}.csv`;

          const res = await upload_campaign_csv(fileContent, fileName);

          resolve({ csv_key: fileName, csv_url: res.Location });
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = err => reject(err);

      reader.readAsText(file);
    }
  });

export const handleFileUpload = async ({ file, userId, templateId }) => {
  try {
    const res = await uploadFile({ file, userId, templateId });
    return { data: res, error: null };
  } catch {
    return { data: null, error: true };
  }
};

export default {};
